import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  TablePagination,
  Pagination,
  Container,
} from "@mui/material";
import {
  Search,
  Warning,
  Inventory as InventoryIcon,
  Refresh,
  Visibility,
} from "@mui/icons-material";
import { inventoryAPI } from "../../services/API";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

export const InvInventory = () => {
  const [stockSummary, setStockSummary] = useState([]);
  const [expiryAlerts, setExpiryAlerts] = useState([]);
  const [dashboardCounts, setDashboardCounts] = useState({
    totalItems: 0,
    lowStockItems: 0,
    adequateStockItems: 0,
    expiryAlerts: {
      total: 0,
      critical: 0,
      high: 0,
      warning: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [lowStockFilter, setLowStockFilter] = useState(false);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Expiry alerts pagination
  const [expiryPage, setExpiryPage] = useState(0);
  const [expiryRowsPerPage, setExpiryRowsPerPage] = useState(6);
  const [totalExpiryAlerts, setTotalExpiryAlerts] = useState(0);

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Fetch all data on component mount
  useEffect(() => {
    fetchDashboardCounts();
    fetchInventoryData();
    fetchExpiryAlerts();
  }, []);

  // Fetch inventory data when filters or pagination change
  useEffect(() => {
    fetchInventoryData();
  }, [page, rowsPerPage, searchTerm, lowStockFilter]);

  useEffect(() => {
    fetchExpiryAlerts();
  }, [expiryPage, expiryRowsPerPage]);

  const fetchDashboardCounts = async () => {
    try {
      const response = await inventoryAPI.getDashboardCounts();
      setDashboardCounts(response.data.data);
    } catch (error) {
      enqueueSnackbar("Error fetching dashboard counts", { variant: "error" });
    }
  };

  const fetchInventoryData = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      // Add search term if provided
      if (searchTerm) {
        params.search = searchTerm;
      }

      // Add low stock filter if enabled
      if (lowStockFilter) {
        params.lowStockOnly = 'true';
      }

      const response = await inventoryAPI.getStockSummary(params);

      setStockSummary(response.data.data.stockSummary);
      setTotalItems(response.data.data.pagination.total);
    } catch (error) {
      enqueueSnackbar("Error fetching inventory data", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchExpiryAlerts = async () => {
    try {
      const params = {
        page: expiryPage + 1,
        limit: expiryRowsPerPage,
      };

      const response = await inventoryAPI.getExpiryAlerts(params);
      setExpiryAlerts(response.data.data.alerts);
      setTotalExpiryAlerts(response.data.data.pagination.total);
    } catch (error) {
      enqueueSnackbar("Error fetching expiry alerts", { variant: "error" });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExpiryPageChange = (event, newPage) => {
    setExpiryPage(newPage);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleLowStockFilter = () => {
    setLowStockFilter(!lowStockFilter);
    setPage(0);
  };

  const handleRefresh = () => {
    fetchDashboardCounts();
    fetchInventoryData();
    fetchExpiryAlerts();
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: "error",
      high: "warning",
      warning: "info"
    };
    return colors[severity] || "info";
  };

  if (loading && page === 0) {
    return <LinearProgress />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 6,
          }}
        >
          <Typography variant="h3" fontWeight="bold" color="primary.main">
            📦 Inventory Management
          </Typography>
          <Button
            startIcon={<Refresh />}
            onClick={handleRefresh}
            variant="contained"
            size="large"
            sx={{
              borderRadius: 3,
              px: 3,
              py: 1
            }}
          >
            Refresh Data
          </Button>
        </Box>

        {/* Main Alert */}
        {dashboardCounts.expiryAlerts.total > 0 && (
          <Alert
            severity="warning"
            sx={{
              mb: 4,
              borderRadius: 3,
              fontSize: '1.1rem',
              py: 2,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
            action={
              <Button
                color="inherit"
                size="large"
                onClick={() => setExpiryPage(0)}
                sx={{ fontWeight: 'bold' }}
              >
                View All
              </Button>
            }
          >
            <Typography variant="h6" fontWeight="bold">
              {dashboardCounts.expiryAlerts.total} items expiring soon
              {dashboardCounts.expiryAlerts.critical > 0 &&
                ` (${dashboardCounts.expiryAlerts.critical} critical)`}
            </Typography>
          </Alert>
        )}

        {/* Summary Cards */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardContent sx={{ textAlign: "center", py: 4 }}>
                <InventoryIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h2" fontWeight="bold" color="primary.main">
                  {dashboardCounts.totalItems}
                </Typography>
                <Typography variant="h5" color="text.secondary" fontWeight="medium">
                  Total Items
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardContent sx={{ textAlign: "center", py: 4 }}>
                <Warning color="warning" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h2" fontWeight="bold" color="warning.main">
                  {dashboardCounts.lowStockItems}
                </Typography>
                <Typography variant="h5" color="text.secondary" fontWeight="medium">
                  Low Stock Items
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardContent sx={{ textAlign: "center", py: 4 }}>
                <Warning color="error" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h2" fontWeight="bold" color="error.main">
                  {dashboardCounts.expiryAlerts.critical}
                </Typography>
                <Typography variant="h5" color="text.secondary" fontWeight="medium">
                  Critical Expiry
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <CardContent sx={{ textAlign: "center", py: 4 }}>
                <Box sx={{ fontSize: 60, mb: 2, color: 'success.main' }}>
                  ✓
                </Box>
                <Typography variant="h2" fontWeight="bold" color="success.main">
                  {dashboardCounts.adequateStockItems}
                </Typography>
                <Typography variant="h5" color="text.secondary" fontWeight="medium">
                  Adequate Stock
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filter Section */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: 2
          }}
        >
          <CardContent sx={{ py: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search items by name, brand, or generic name..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  size="medium"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      height: 56
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 28 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant={lowStockFilter ? "contained" : "outlined"}
                  onClick={handleLowStockFilter}
                  color={lowStockFilter ? "warning" : "primary"}
                  fullWidth
                  size="large"
                  sx={{
                    borderRadius: 2,
                    height: 56,
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }}
                >
                  {lowStockFilter ? "✓ Low Stock Only" : "Show Low Stock"}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Inventory Table Section */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: 3,
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {/* Table Header */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 3,
                backgroundColor: 'primary.main',
                color: 'white'
              }}
            >
              <Typography variant="h4" fontWeight="bold">
                📋 Item Stock Summary
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Showing {stockSummary.length} of {totalItems} items
              </Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell sx={{ fontSize: '1.2rem', fontWeight: 'bold', py: 3 }}>Item Details</TableCell>
                    <TableCell sx={{ fontSize: '1.2rem', fontWeight: 'bold', py: 3 }}>Generic Name</TableCell>
                    <TableCell align="center" sx={{ fontSize: '1.2rem', fontWeight: 'bold', py: 3 }}>Total Stock</TableCell>
                    <TableCell align="center" sx={{ fontSize: '1.2rem', fontWeight: 'bold', py: 3 }}>Batches</TableCell>
                    <TableCell align="center" sx={{ fontSize: '1.2rem', fontWeight: 'bold', py: 3 }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontSize: '1.2rem', fontWeight: 'bold', py: 3 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockSummary.map((stockItem) => (
                    <TableRow
                      key={stockItem.item._id}
                      hover
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <TableCell sx={{ py: 2.5 }}>
                        <Box>
                          <Typography variant="h6" fontWeight="bold">
                            {stockItem.item.name}
                          </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                            {stockItem.item.brand}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }}>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                          {stockItem.item.genericName}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2.5 }}>
                        <Typography
                          variant="h5"
                          fontWeight="bold"
                          color={stockItem.lowStock ? "warning.main" : "success.main"}
                        >
                          {stockItem.totalStock}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2.5 }}>
                        <Chip
                          label={stockItem.batches.length}
                          size="medium"
                          variant="outlined"
                          sx={{ fontSize: '1rem', fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2.5 }}>
                        <Chip
                          label={stockItem.lowStock ? "Low Stock" : "Adequate"}
                          color={stockItem.lowStock ? "warning" : "success"}
                          size="medium"
                          sx={{
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            minWidth: 100
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2.5 }}>
                        <Button
                          startIcon={<Visibility />}
                          size="large"
                          variant="contained"
                          onClick={() => {
                            if (stockItem.item._id) {
                              navigate(`/hospital/hospital-inventory/items/${stockItem.item._id}`);
                            }
                          }}
                          sx={{
                            borderRadius: 2,
                            fontWeight: 'bold',
                            px: 3
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Table Pagination */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalItems}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '1.1rem'
                }
              }}
            />

            {stockSummary.length === 0 && !loading && (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
                  📭 No items found
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Try adjusting your search criteria or filters
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Expiry Alerts Section */}
        {expiryAlerts.length > 0 && (
          <Card
            sx={{
              mt: 6,
              borderRadius: 3,
              boxShadow: 3,
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {/* Expiry Alerts Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 3,
                  backgroundColor: 'warning.light',
                  color: 'warning.dark'
                }}
              >
                <Typography variant="h4" fontWeight="bold">
                  ⚠️ Expiry Alerts
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                  Showing {expiryAlerts.length} of {totalExpiryAlerts} alerts
                </Typography>
              </Box>

              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {expiryAlerts.map((alert) => (
                    <Grid item xs={12} sm={6} md={4} key={alert._id}>
                      <Alert
                        severity={getSeverityColor(alert.severity)}
                        icon={false}
                        sx={{
                          borderRadius: 3,
                          py: 2,
                          height: '100%',
                          '& .MuiAlert-message': { width: '100%' }
                        }}
                      >
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                          {alert.item?.name}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>Batch:</strong> {alert.batchNumber}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>Expires in:</strong> {alert.daysUntilExpiry} days
                        </Typography>
                        <Typography variant="body1">
                          <strong>Stock:</strong> {alert.availableQuantity} units
                        </Typography>
                      </Alert>
                    </Grid>
                  ))}
                </Grid>

                {/* Expiry Alerts Pagination */}
                {totalExpiryAlerts > expiryRowsPerPage && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                      count={Math.ceil(totalExpiryAlerts / expiryRowsPerPage)}
                      page={expiryPage + 1}
                      onChange={(event, value) => handleExpiryPageChange(event, value - 1)}
                      color="primary"
                      size="large"
                      sx={{
                        '& .MuiPaginationItem-root': {
                          fontSize: '1.1rem',
                          minWidth: 40,
                          height: 40
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};